'use client';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { GetTokenDialog, useViduToken } from '@/app/components/shared/UserCenter';
import { VIDU_SKILL_GITHUB_URL } from '@/app/constants';
import { useClaw, useCopy } from '@/app/hooks';
import { useRouter } from '@/i18n/routing';

const HERO_BG = 'https://image01-aliyun.vidu.zone/vidu/media-asset/claw-product-header-img-206e7e18-aaaf5e43.webp';
const CTA_BG = 'https://image01-aliyun.vidu.zone/vidu/media-asset/claw-product-img-1-09d87dba.webp';

interface ScenarioItem {
  title: string;
  description: string;
  image: string;
}

const scenarios: ScenarioItem[] = [
  {
    title: '社媒创作者',
    description:
      '针对需要每日更新短视频但缺乏制作预算和精力的创作者。只需输入“产品展示”或“夏日大促”等简单想法，Vidu Claw 即可自动生成包含分镜、脚本与配乐的完整短片。',
    image: 'https://image01-aliyun.vidu.zone/vidu/media-asset/vidu-claw-product-img-1-9e3281da.webp',
  },
  {
    title: '电商运营',
    description:
      '面对拥有成千上万商品页面的电商团队，静态图已难以吸引注意力。Vidu Claw 能将产品图瞬间转化为生动的实景短视频，真实展示产品魅力。',
    image: 'https://image01-aliyun.vidu.zone/vidu/media-asset/vidu-claw-product-img-2-0e08b7e6.webp',
  },
  {
    title: '内容增长专家',
    description:
      '助力高增长公司的营销团队。Vidu Claw 能自动读取产品文档，提炼创意点并转化为适配 LinkedIn 或 YouTube 风格的演示视频，实现从文档到内容的无缝衔接。',
    image: 'https://image01-aliyun.vidu.zone/vidu/media-asset/vidu-claw-product-img-3-052cf8ce.webp',
  },
  {
    title: '个体经营者',
    description:
      '为缺乏设计技能和专业团队的小店主赋能。Vidu Claw 能将碎片化的想法转化为专业的营销视频和社媒素材，让产品推广变得简单高效。',
    image: 'https://image01-aliyun.vidu.zone/vidu/media-asset/vidu-claw-product-img-4-a289b0fc.webp',
  },
];

export function ViduClawChina() {
  const { copy } = useCopy();
  const router = useRouter();
  const { getMyClaws } = useClaw();
  const {
    open: openTokenDialog,
    setOpen: setOpenTokenDialog,
    token: viduToken,
    requestToken,
    handleReset,
  } = useViduToken({
    onResetTokenSuccess: () => {
      getMyClaws();
    },
  });

  const openClaw = () => {
    router.push('/claw');
  };

  return (
    <div className="w-full bg-black pb-10 text-white">
      <section
        className="mx-auto flex h-[624px] max-w-[1920px] items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundPosition: 'center top',
          backgroundSize: 'cover',
        }}
      />

      <section className="-mt-28 relative overflow-hidden pb-20 sm:pt-28 sm:pb-10">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center px-6 text-center sm:px-4">
          <p className="mt-6 text-center font-semibold text-[22px] text-system-white80 tracking-[32px] sm:text-base sm:tracking-[2px]">
            你的第一个AI创意员工
          </p>
          <div className="mt-8 flex items-center gap-4 sm:w-full sm:flex-col">
            <div
              onClick={() => {
                router.push('/vidu-claw/showcase');
              }}
              className="inline-flex h-12 w-[220px] cursor-pointer items-center justify-center rounded-[100px] border border-system-white12 bg-system-white32 font-medium text-white hover:bg-system-hover01"
            >
              查看案例
            </div>
            <a
              href="#skills"
              className="inline-flex h-12 w-[220px] items-center justify-center rounded-[100px] border border-system-white12 bg-system-white32 font-medium text-white hover:bg-system-hover01"
            >
              使用Vidu Skills
            </a>
            <div
              className="inline-flex h-12 w-[220px] cursor-pointer items-center justify-center rounded-[100px] bg-[#0086FF] font-medium text-white hover:bg-[#0d92ff]"
              onClick={() => {
                openClaw();
              }}
            >
              使用 Vidu Claw
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1200px] px-6 pt-16 pb-12 sm:px-4 sm:pt-10">
        <h2 className="text-center font-semibold text-4xl leading-[1.3] sm:text-[28px]">
          雇佣 AI 创意员工 Vidu Claw，输入想法即刻解锁无限场景
        </h2>
      </section>

      <section className="mx-auto flex w-full max-w-[1200px] flex-col gap-16 py-6 sm:px-4">
        {scenarios.map((item, index) => (
          <article key={item.title} className="grid grid-cols-2 items-center gap-8 sm:grid-cols-1">
            <div className={`${index % 2 === 0 ? 'order-2 sm:order-1' : ''} mb-12`}>
              <h3 className="font-semibold text-[32px] sm:text-2xl">{item.title}</h3>
              <p className="mt-4 text-base text-system-white64 leading-7">{item.description}</p>
            </div>
            <div className={`${index % 2 === 0 ? 'order-1 sm:order-2' : ''}`}>
              <img
                src={item.image}
                alt={item.title}
                loading="lazy"
                decoding="async"
                className="h-[320px] w-full overflow-hidden rounded-[20px] object-cover sm:aspect-video sm:h-auto"
              />
            </div>
          </article>
        ))}
      </section>

      <section id="skills" className="mx-auto w-full max-w-[1200px] py-10 pt-16 sm:px-4">
        <div className="text-center font-semibold text-4xl sm:text-[28px]">集成 Vidu Skills，唤醒龙虾的创作能力</div>
        <div className="mt-8 rounded-16 rounded-24 bg-white/[0.08] p-10 sm:p-5">
          <div className="space-y-10">
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-xl sm:text-lg">
                <span className="font-bold text-[#0086FF]">1.</span>
                <span className="font-medium text-white">在Claw中通过对话安装vidu-skills</span>
              </p>
              <div className="relative">
                <pre className="overflow-x-auto rounded-16 border border-system-white12 bg-system-black36 px-6 py-5 text-sm text-system-white64 sm:px-4 sm:py-4">
                  帮我安装Skills {VIDU_SKILL_GITHUB_URL}
                </pre>
                <button
                  type="button"
                  aria-label="Copy install command"
                  className="-translate-y-1/2 absolute top-1/2 right-5 sm:right-3"
                  onClick={() => {
                    copy(`帮我安装Skills ${VIDU_SKILL_GITHUB_URL}`);
                  }}
                >
                  <BaseLineCopy className="size-4 text-system-white" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <p className="flex items-center gap-2 text-xl sm:text-lg">
                  <span className="font-bold text-[#0086FF]">2.</span>
                  <span className="font-medium text-white">获取VIDU_TOKEN，并配置环境变量</span>
                </p>
                <div
                  className="cursor-pointer rounded-16 border border-[rgba(0,134,255,0.40)] bg-[rgba(0,134,255,0.24)] px-4 py-1.5 text-sm text-white"
                  onClick={requestToken}
                >
                  获取VIDU_TOKEN
                </div>
              </div>
              <div className="relative">
                <pre className="overflow-x-auto rounded-16 border border-system-white12 bg-system-black36 px-6 py-5 text-sm text-system-white64 sm:px-4 sm:py-4">{`export VIDU_TOKEN=your_token
export VIDU_BASE_URL=https://service.vidu.cn`}</pre>
                <button
                  type="button"
                  aria-label="Copy environment variables"
                  className="absolute top-6 right-5 sm:top-4 sm:right-3"
                  onClick={() => {
                    copy('export VIDU_TOKEN=your_token\nexport VIDU_BASE_URL=https://service.vidu.cn');
                  }}
                >
                  <BaseLineCopy className="size-4 text-system-white" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="space-y-1">
                <p className="flex items-center gap-2 text-xl sm:text-lg">
                  <span className="font-bold text-[#0086FF]">3.</span>
                  <span className="font-medium text-white">集成成功后，你可以直接通过对话调用</span>
                </p>
              </div>
              <div className="relative">
                <pre className="overflow-x-auto rounded-16 border border-system-white12 bg-system-black36 px-6 py-5 text-sm text-system-white64 sm:px-4 sm:py-4">
                  调用Vidu帮我生成一段‘赛博朋克赛车在霓虹城市穿梭’的视频。
                </pre>
                <button
                  type="button"
                  aria-label="Copy sample prompt"
                  className="-translate-y-1/2 absolute top-1/2 right-5 sm:right-3"
                  onClick={() => {
                    copy('调用 Vidu帮我生成一段‘赛博朋克赛车在霓虹城市穿梭’的视频。');
                  }}
                >
                  <BaseLineCopy className="size-4 text-system-white" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] rounded-3xl bg-[rgba(186,169,127,0.16)] p-10 text-[#BAA97F] text-base">
        提示：ViduClaw目前处于申请测试阶段，用户提交申请并审核通过后即可使用；Vidu
        Skills面向所有Vidu订阅会员开放。两项功能均根据内容任务类型扣除相应积分。
      </section>

      <section
        className="mt-28 py-20 sm:py-14"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${CTA_BG})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      >
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center px-6 text-center sm:px-4">
          <h2 className="font-semibold text-5xl sm:text-3xl">Vidu Claw</h2>
          <p className="mt-3 text-system-white80">你的第一个AI创意员工</p>
          <div className="mt-6 flex gap-4 sm:w-full sm:flex-col">
            <div
              onClick={() => {
                router.push('/vidu-claw/showcase');
              }}
              className="inline-flex h-12 w-[220px] cursor-pointer items-center justify-center rounded-[100px] border border-system-white12 bg-system-white32 font-medium text-white hover:bg-system-hover01"
            >
              查看案例
            </div>
            <a
              href="#skills"
              className="inline-flex h-12 w-[220px] items-center justify-center rounded-[100px] border border-system-white12 bg-system-white32 font-medium text-white hover:bg-system-hover01"
            >
              使用Vidu Skills
            </a>
            <div
              className="inline-flex h-12 w-[220px] cursor-pointer items-center justify-center rounded-[100px] bg-[#0086FF] font-medium text-white hover:bg-[#0d92ff]"
              onClick={() => {
                openClaw();
              }}
            >
              使用 Vidu Claw
            </div>
          </div>
        </div>
      </section>
      <GetTokenDialog
        open={openTokenDialog}
        onOpenChange={setOpenTokenDialog}
        token={viduToken}
        onReset={handleReset}
      />
    </div>
  );
}
